/**
 * Single source of truth for the canvas editor's keyboard shortcuts, used by
 * both the in-canvas help dialog (CanvasShortcutsHelp) and the contextual hints
 * on the text-alignment control in ScenePropertiesPanel.
 *
 * The actual key handling lives in useEditorEvents.ts; this module only
 * describes the text-align / font-size bindings surfaced in the properties
 * panel. The full shortcut reference (selection, transform, arrange, view,
 * opacity, etc.) is rendered by CanvasShortcutsHelp. Keep all three in sync
 * when adding a shortcut. Note: snapping is suppressed with Cmd/Ctrl during a
 * drag/resize (Alt is reserved for duplicate-drag and resize-from-center).
 *
 * Modifier names are plain ASCII ('Cmd' / 'Opt' vs 'Ctrl' / 'Alt') rather than
 * the macOS glyphs to stay within the repo's no-decorative-unicode rule and to
 * avoid source-encoding pitfalls.
 */

export type TextAlign = 'left' | 'center' | 'right' | 'justify';

export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return true;
  return /Mac|iPhone|iPad/i.test(navigator.userAgent);
}

export interface ModifierLabels {
  cmd: string;
  alt: string;
  shift: string;
}

/** Platform-appropriate modifier-key labels for display. */
export function modifierLabels(): ModifierLabels {
  return isMacPlatform()
    ? { cmd: 'Cmd', alt: 'Opt', shift: 'Shift' }
    : { cmd: 'Ctrl', alt: 'Alt', shift: 'Shift' };
}

const TEXT_ALIGN_KEY: Record<TextAlign, string> = {
  left: 'L',
  center: 'T',
  right: 'R',
  justify: 'J',
};

/** The bare letter key for a given alignment (matches useEditorEvents e.code). */
export function textAlignKey(align: TextAlign): string {
  return TEXT_ALIGN_KEY[align];
}

/** Human-readable shortcut for an alignment, e.g. "Cmd Opt T". */
export function textAlignShortcut(align: TextAlign): string {
  const m = modifierLabels();
  return `${m.cmd} ${m.alt} ${TEXT_ALIGN_KEY[align]}`;
}

/** Human-readable hint for the font-size resize shortcut. */
export function fontSizeShortcut(): string {
  const m = modifierLabels();
  return `Resize: ${m.cmd} ${m.shift} > / <  (hold ${m.alt} for 10)`;
}
